import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { SidebarComponent } from '@app/+authenticated/shared/sidebar/sidebar.component';
import { fieldChangeWithStart } from '@app/forms/form.helper';
import { selectAllowedDashboardFilters } from '@app/reducers/page-filters/page-filters-dashboard.selectors';
import { useHrProOrUpsellVisible } from '@app/services/hr-pro-upsell/use-hr-pro-or-upsell-visible';
import { select, Store } from '@ngrx/store';
import { selectAccountHasHrPlus } from '@reducers/account/account.selector';
import { AppState } from '@reducers/interfaces';
import {
  pagedashboardFilterViewConfig,
  withoutHiddenWidgets,
} from '@reducers/page-filters/page-filters-dashboard.config';
import { DashboardFilterState } from '@reducers/page-filters/page-filters.model';
import { filterPersistenceDebounceTime, PageFiltersService } from '@reducers/page-filters/page-filters.service';
import { mgcIconLockRegular } from '@sb/mgc-icons/mgcIcon-lock_regular.icon';
import { FeatureService } from '@sb/shared/feature';
import { IconRegistry } from '@sb/ui/icon';
import { Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';

const hrProWidgets = new Set(
  Object.keys(pagedashboardFilterViewConfig).filter((type) => pagedashboardFilterViewConfig[type].hrPro),
);

@Component({
  standalone: false,
  selector: 'my-dashboard-sidebar',
  templateUrl: './my-dashboard-sidebar.component.html',
})
export class MyDashboardSidebarComponent implements OnInit, OnDestroy {
  private sidebar = inject(SidebarComponent);
  private store = inject<Store<AppState>>(Store);
  private filterService = inject(PageFiltersService);
  #featureService = inject(FeatureService);
  #hrProVisible = useHrProOrUpsellVisible();

  private dataSubs = new Subscription();
  checkboxesFormGroup = new FormGroup<Record<string, FormControl<boolean | null>>>({});

  controlsOrder: string[] = [];
  hrProWidgets = hrProWidgets;
  hasHrPro = this.store.selectSignal(selectAccountHasHrPlus);

  private dashboardFilters: DashboardFilterState;

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconLockRegular]);
  }

  ngOnInit() {
    this.dataSubs.add(
      this.store.pipe(select(selectAllowedDashboardFilters), take(1)).subscribe((allowedFilters) => {
        const filters = withoutHiddenWidgets(allowedFilters, {
          isEnabled: (flag) => this.#featureService.isEnabled(flag),
          hrProVisible: this.#hrProVisible(),
        });
        this.dashboardFilters = filters;

        Object.entries(filters).forEach(([key, value]) => {
          this.controlsOrder.push(key);
          const control = new FormControl(value.visibility);
          this.checkboxesFormGroup.addControl(key, control);
        });
      }),
    );

    this.dataSubs.add(
      fieldChangeWithStart(this.checkboxesFormGroup)
        .pipe(debounceTime(filterPersistenceDebounceTime))
        .subscribe((formValue) => {
          Object.entries(formValue).forEach(([key, val]) => {
            this.dashboardFilters[key] = {
              ...this.dashboardFilters[key],
              visibility: val as boolean,
            };
          });
          this.filterService.setFilters('dashboard', { ...this.dashboardFilters });
        }),
    );
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  close() {
    this.sidebar.close();
  }
}
